<template>
    <div
        class="entry-calculators d-inline-flex align-center flex-wrap ga-2"
        @click.stop
        @mousedown.stop
        @touchstart.stop
    >
        <div ref="anchor" class="entry-calculators__anchor d-inline-flex">
            <v-menu
                v-model="listOpen"
                location="bottom end"
                offset="6"
                :close-on-content-click="true"
                :open-on-click="false"
            >
                <template #activator="{ props: menuProps }">
                    <v-btn
                        v-bind="activatorBindings(menuProps)"
                        icon
                        size="small"
                        variant="text"
                        color="yellow"
                        data-cy="entry-calculators-menu-button"
                        @click="onCalculatorIconClick"
                    >
                        <v-icon>mdi-calculator</v-icon>
                        <v-tooltip activator="parent" location="bottom">
                            {{ $t("entries.calculatorsMenuTooltip") }}
                        </v-tooltip>
                    </v-btn>
                </template>

                <v-list
                    density="compact"
                    bg-color="primary"
                    class="entry-calculators__list"
                >
                    <v-list-item
                        :title="$t('entries.calculatorMenuFloorArea')"
                        prepend-icon="mdi-ruler-square"
                        @click="openPanel('floor')"
                    />
                    <v-list-item
                        v-if="showDuration"
                        :title="$t('entries.calculatorMenuDuration')"
                        prepend-icon="mdi-timer-outline"
                        @click="openPanel('duration')"
                    />
                    <v-list-item
                        :title="$t('entries.calculatorMenuFuel')"
                        prepend-icon="mdi-gas-station"
                        @click="openPanel('fuel')"
                    />
                </v-list>
            </v-menu>

            <v-menu
                :model-value="activePanel === 'floor'"
                location="bottom end"
                offset="6"
                :close-on-content-click="false"
                :scrim="false"
                :activator="anchorEl"
                @update:model-value="onPanelOpenChange('floor', $event)"
            >
                <FloorAreaPanel
                    ref="floorPanel"
                    :length-cm="lengthCm"
                    :width-cm="widthCm"
                    :package-count="packageCount"
                    @update:length-cm="$emit('update:lengthCm', $event)"
                    @update:width-cm="$emit('update:widthCm', $event)"
                    @update:package-count="$emit('update:packageCount', $event)"
                    @apply="onFloorApply"
                />
            </v-menu>

            <v-menu
                v-if="showDuration"
                :model-value="activePanel === 'duration'"
                location="bottom end"
                offset="6"
                :close-on-content-click="false"
                :scrim="false"
                :activator="anchorEl"
                @update:model-value="onPanelOpenChange('duration', $event)"
            >
                <TripDurationPanel
                    ref="durationPanel"
                    :entry="entry"
                    :show-distance-input="false"
                    :show-apply-button="entry?.form_type !== 'truck'"
                    :speed-kmh="speedKmh"
                    :speed-readonly="entry?.form_type === 'truck'"
                    @update:speed-kmh="$emit('update:speedKmh', $event)"
                    @apply="onDurationApply"
                />
            </v-menu>

            <v-menu
                :model-value="activePanel === 'fuel'"
                location="bottom end"
                offset="6"
                :close-on-content-click="false"
                :scrim="false"
                :activator="anchorEl"
                @update:model-value="onPanelOpenChange('fuel', $event)"
            >
                <FuelCalculatorPanel
                    ref="fuelPanel"
                    :price-per-liter="pricePerLiter"
                    :distance-km="distanceKm"
                    :consumption-l-per100-km="consumptionLPer100Km"
                    @update:price-per-liter="$emit('update:pricePerLiter', $event)"
                    @update:distance-km="$emit('update:distanceKm', $event)"
                    @update:consumption-l-per100-km="
                        $emit('update:consumptionLPer100Km', $event)
                    "
                    @apply="onFuelApply"
                />
            </v-menu>
        </div>

        <button
            v-if="appliedFloorAreaLabel"
            type="button"
            class="entry-calculators__chip"
            @click="openPanel('floor')"
        >
            <span class="entry-calculators__chip-label"
                >{{ $t("entries.floorAreaUnit") }}:</span
            >
            <span class="entry-calculators__chip-value">{{
                appliedFloorAreaLabel
            }}</span>
            <v-tooltip activator="parent" location="bottom">
                {{ $t("entries.floorAreaAppliedTooltip") }}
            </v-tooltip>
        </button>

        <button
            v-if="durationChipLabel"
            type="button"
            class="entry-calculators__chip"
            @click="openPanel('duration')"
        >
            <span class="entry-calculators__chip-label"
                >{{ $t("entries.tripDuration") }}:</span
            >
            <span class="entry-calculators__chip-value">{{
                durationChipLabel
            }}</span>
            <v-tooltip activator="parent" location="bottom">
                {{ $t("entries.tripDurationTooltip") }}
            </v-tooltip>
        </button>

        <button
            v-if="appliedFuelCostLabel"
            type="button"
            class="entry-calculators__chip"
            @click="openPanel('fuel')"
        >
            <span class="entry-calculators__chip-label"
                >{{ $t("formFields.fuelCostResult") }}:</span
            >
            <span class="entry-calculators__chip-value">{{
                appliedFuelCostLabel
            }}</span>
            <v-tooltip activator="parent" location="bottom">
                {{ $t("entries.fuelCalculatorAppliedTooltip") }}
            </v-tooltip>
        </button>
    </div>
</template>

<script>
import FloorAreaPanel from "./FloorAreaPanel.vue";
import TripDurationPanel from "./TripDurationPanel.vue";
import FuelCalculatorPanel from "./FuelCalculatorPanel.vue";
import { formatFloorAreaCopyValue } from "@/utils/floorAreaCalculation";
import { formatFuelCostLabel } from "@/utils/fuelCostCalculation";

export default {
    name: "EntryCalculatorsMenu",
    components: {
        FloorAreaPanel,
        TripDurationPanel,
        FuelCalculatorPanel,
    },
    props: {
        entry: {
            type: Object,
            required: true,
        },
        showDuration: {
            type: Boolean,
            default: false,
        },
        durationChipLabel: {
            type: String,
            default: "",
        },
        lengthCm: {
            type: [String, Number],
            default: "",
        },
        widthCm: {
            type: [String, Number],
            default: "",
        },
        packageCount: {
            type: [String, Number],
            default: "1",
        },
        appliedFloorAreaLabel: {
            type: String,
            default: "",
        },
        speedKmh: {
            type: [String, Number],
            default: "",
        },
        pricePerLiter: {
            type: [String, Number],
            default: "",
        },
        distanceKm: {
            type: [String, Number],
            default: "",
        },
        consumptionLPer100Km: {
            type: [String, Number],
            default: "",
        },
        appliedFuelCostLabel: {
            type: String,
            default: "",
        },
        activePanel: {
            type: String,
            default: null,
            validator: (value) =>
                value == null ||
                ["floor", "duration", "fuel"].includes(value),
        },
    },
    emits: [
        "update:activePanel",
        "update:lengthCm",
        "update:widthCm",
        "update:packageCount",
        "update:speedKmh",
        "update:pricePerLiter",
        "update:distanceKm",
        "update:consumptionLPer100Km",
        "apply-floor",
        "apply-duration",
        "apply-fuel",
    ],
    data() {
        return {
            listOpen: false,
            anchorEl: undefined,
            dismissApplyLock: null,
        };
    },
    watch: {
        activePanel(next, prev) {
            // Parent closed the panel (e.g. click inside the big card).
            if (prev && !next) {
                this.applyOnDismiss(prev);
            }
        },
    },
    mounted() {
        this.anchorEl = this.$refs.anchor;
    },
    methods: {
        activatorBindings(menuProps) {
            // Keep aria/ref bindings from v-menu, but control open via our click handler.
            const { onClick, ...rest } = menuProps || {};
            return rest;
        },
        panelRefName(panel) {
            if (panel === "floor") return "floorPanel";
            if (panel === "duration") return "durationPanel";
            return "fuelPanel";
        },
        applyOnDismiss(panel) {
            // Duration only applies via its Apply button.
            if (!panel || panel === "duration") return;
            if (this.dismissApplyLock === panel) return;
            this.dismissApplyLock = panel;

            if (panel === "floor") {
                const resultLabel = formatFloorAreaCopyValue(
                    this.lengthCm,
                    this.widthCm,
                    this.packageCount
                );
                if (resultLabel) {
                    this.$emit("apply-floor", {
                        lengthCm: this.lengthCm,
                        widthCm: this.widthCm,
                        packageCount: this.packageCount,
                        resultLabel,
                    });
                }
            } else if (panel === "fuel") {
                const resultLabel = formatFuelCostLabel({
                    distanceKm: this.distanceKm,
                    consumptionLPer100Km: this.consumptionLPer100Km,
                    pricePerLiter: this.pricePerLiter,
                });
                if (resultLabel) {
                    this.$emit("apply-fuel", {
                        pricePerLiter: this.pricePerLiter,
                        distanceKm: this.distanceKm,
                        consumptionLPer100Km: this.consumptionLPer100Km,
                        resultLabel,
                    });
                }
            }

            this.$nextTick(() => {
                if (this.dismissApplyLock === panel) {
                    this.dismissApplyLock = null;
                }
            });
        },
        closeActivePanel() {
            if (!this.activePanel) return;
            const panel = this.activePanel;
            const panelComp = this.$refs[this.panelRefName(panel)];
            if (panelComp?.shouldSuppressCloseApply?.()) {
                return;
            }
            this.applyOnDismiss(panel);
            this.$emit("update:activePanel", null);
        },
        onCalculatorIconClick(event) {
            event?.stopPropagation?.();
            event?.preventDefault?.();

            if (this.listOpen) {
                this.listOpen = false;
                return;
            }

            // Close any open calculator first, then open the picker list.
            if (this.activePanel) {
                this.closeActivePanel();
                this.$nextTick(() => {
                    this.listOpen = true;
                });
                return;
            }

            this.listOpen = true;
        },
        openPanel(panel) {
            this.listOpen = false;
            this.$nextTick(() => {
                this.$emit("update:activePanel", panel);
            });
        },
        onPanelOpenChange(panel, open) {
            if (!open) {
                const panelComp = this.$refs[this.panelRefName(panel)];
                if (panelComp?.shouldSuppressCloseApply?.()) {
                    this.$emit("update:activePanel", panel);
                    return;
                }
                // Apply from current props before the panel unmounts.
                this.applyOnDismiss(panel);
                if (this.activePanel === panel) {
                    this.$emit("update:activePanel", null);
                }
                return;
            }
            this.$emit("update:activePanel", panel);
        },
        onFloorApply(payload) {
            this.dismissApplyLock = "floor";
            this.$emit("apply-floor", payload);
            this.$emit("update:activePanel", null);
            this.$nextTick(() => {
                this.dismissApplyLock = null;
            });
        },
        onDurationApply(payload) {
            this.$emit("apply-duration", payload);
            this.$emit("update:activePanel", null);
        },
        onFuelApply(payload) {
            this.dismissApplyLock = "fuel";
            this.$emit("apply-fuel", payload);
            this.$emit("update:activePanel", null);
            this.$nextTick(() => {
                this.dismissApplyLock = null;
            });
        },
    },
};
</script>

<style scoped>
.entry-calculators__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    padding: 4px 10px;
    border: 0;
    border-radius: 999px;
    background: #ffeb3b;
    color: #0d47a1;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.2;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.entry-calculators__chip:hover {
    filter: brightness(0.97);
}

.entry-calculators__chip-label,
.entry-calculators__chip-value {
    color: inherit;
}

.entry-calculators__list {
    border: 1px solid rgba(255, 235, 59, 0.35);
    min-width: 220px;
}

.entry-calculators__list :deep(.v-list-item) {
    color: #fff;
}

.entry-calculators__list :deep(.v-list-item:hover) {
    background: rgba(255, 235, 59, 0.12);
}

.entry-calculators__list :deep(.v-list-item__spacer) {
    width: 12px;
}
</style>
